import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { ProductWarrantyMapping, WarrantyTemplate } from '@/admin/types/warranties'

export interface WarrantyMappingItemWrite {
  warranty_template_uuid: string
  default_option_uuid?: string | null
  display_order?: number
}

export interface WarrantyMappingCreate {
  level: string
  product_uuid?: string | null
  category_uuid?: string | null
  product_template_uuid?: string | null
  priority?: number
  mapping_items?: WarrantyMappingItemWrite[]
}

export interface WarrantyMappingUpdate {
  uuid: string
  priority?: number
  mapping_items?: WarrantyMappingItemWrite[]
}

export function useWarrantyMappings() {
  return useQuery<ProductWarrantyMapping[]>({
    queryKey: ['warranty-mappings'],
    queryFn: () => api.get('/warranties/mappings/').then((r) => r.data),
  })
}

export function useCreateWarrantyMapping() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: WarrantyMappingCreate) =>
      api.post('/warranties/mappings/', data).then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['warranty-mappings'] })
      void queryClient.invalidateQueries({ queryKey: ['resolved-warranties'] })
    },
  })
}

export function useUpdateWarrantyMapping() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, ...data }: WarrantyMappingUpdate) =>
      api.put(`/warranties/mappings/${uuid}/`, data).then((r) => r.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['warranty-mappings'] })
      void queryClient.invalidateQueries({ queryKey: ['resolved-warranties'] })
    },
  })
}

export function useDeleteWarrantyMapping() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => api.delete(`/warranties/mappings/${uuid}/`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['warranty-mappings'] })
      void queryClient.invalidateQueries({ queryKey: ['resolved-warranties'] })
    },
  })
}

export function useResolveAvailableWarranties(productUuid: string | null) {
  return useQuery<WarrantyTemplate[]>({
    queryKey: ['resolved-warranties', productUuid],
    queryFn: () =>
      api
        .get('/warranties/mappings/resolve-available-templates/', {
          params: { product_uuid: productUuid },
        })
        .then((r) => r.data),
    enabled: !!productUuid,
  })
}
